/**
 * Setting entity class
 */

/**
 * SettingFactory
 * returns Setting entity class
 *
 * @class SettingFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Setting} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SettingFactory
 */
/* @ngInject */
export default function SettingFactory ($http, $q, alertLiteSvc, openPopupMixin, EntityCache, Entity) {
  /* === Setting class === */

  /**
   * Setting model
   *
   * @class Setting
   * @classdesc Setting model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.Setting} Setting entity
   */
  function Setting () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Setting.prototype = Object.create(Entity.prototype)
  Setting.prototype.constructor = Setting

  /* === Setting instance methods & fields === */

  Object.assign(Setting.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Setting#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      network: 'Network',
      useSystemDefault: Boolean,
      value: String
    },

    /**
     * url template
     *
     * @memberof models.Setting#
     * @type {String}
     */
    _urlTemplate: 'api/settings'

  })

  /* === Setting static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Setting, Entity)
  Setting._name = 'Setting'
  Setting.init()

  /**
   * Load bunch of settings at once
   *
   * @memberof models.Setting
   * @return {Promise} promise
   */
  Setting.loadAll = function () {
    return Entity.loadAll.apply(this, arguments)
      .then(
        settings =>
          settings
            ? settings.reduce(toMap, Object.create(null))
            : {}
      )
  }

  /**
   * Save bunch of settings at once
   *
   * @memberof models.Setting
   * @param {Object}         settings - Settings entities
   * @param {models.Setting} settings.key
   * @param {Object}         [originals] - original settings entities
   * @param {models.Setting} [originals.key]
   * @param {Object}         generalSettings - Settings entities
   * @param {models.Setting} generalSettings.key
   * @param {Object}         [params] - parameters for url interpolation
   * @return {Promise} promise
   */
  Setting.saveAll = function (settings, originals, generalSettings, params) {
    // create data array, to send to the server
    var data = []
    for (const key in settings) {
      if (!originals || (
        originals[key] && (
          settings[key].value !== originals[key].value ||
              settings[key].useSystemDefault !== originals[key].useSystemDefault))
      ) {
        data.push(settings[key])
      }
    }
    data.forEach(d => {
      if (d.useSystemDefault) {
        d.value = generalSettings[d.name].value
      }
    })

    // return entity object
    const toObject = (cache) => (e) => e._object(cache)

    // update settings, performing PUT request to the server
    var promise = data.length
      ? $http.put(this.prototype.$_urlTemplate(params), data.map(toObject(new EntityCache())))
      : $q.when(null) // if there is no changed data -> just do nothing

    return promise
      .then(response => response)
      .catch((error) => {
        // Settings - General Settings - Payment Settings
        // CR-222 Max activation length improvement in the context of IAP
        const maxActivationLengthPhrase = 'Please, pay attention that Max Activation Length can’t be less than the length that set'
        if (error.data.includes(maxActivationLengthPhrase)) {
          openPopupMixin.openPopup(error.data)
          return $q.reject(error)
        }
        return alertLiteSvc.alertError(error)
      })
  }

  return Setting

  /* === Private helper functions === */

  /**
   * Function to use in Array.prototype.reduce, to reduce array of settings to map
   *
   * @private
   * @memberof mwApp.factory:SettingFactory
   * @param  {Object}        map - result map
   * @param  {models.Setting} s  - setting
   * @return {Object} map
   */
  function toMap (map, s) {
    s.value = guessType(s.value)
    map[s.name] = s
    return map
  }

  /**
   * Guess and change type of value from String
   *
   * @private
   * @memberof mwApp.factory:SettingFactory
   * @param  {*} value      - value
   * @return {*}
   */
  function guessType (value) {
    value = String(value)

    var numberValue = Number(value)
    if (value && !isNaN(numberValue)) {
      return numberValue
    }

    if (value === 'true' || value === 'false') {
      return value === 'true'
    }

    return value
  }
}
